/**
 * CÓMO SE LLAMA UN CULTIVO EN EL IDIOMA DE QUIEN MIRA
 * (05-10-2026, plan cultivos SIEX, F4 «lectores»).
 *
 * `etiquetaCultivo(código, nombre, t, idioma)` sustituye a la heurística de
 * `useCropTranslation`, que comparaba las TRES primeras letras: «Pepino» empieza
 * por «pep», igual que la clave `pepper`, y en inglés, francés, italiano,
 * bambara, árabe, chino y mandinga salía «Pepper», «Poivron», «Peperone»,
 * «Piman»… Un pepino enseñado como pimiento.
 *
 * El orden:
 *   1. El código SIEX del ciclo, si lo tiene. Si no, el resolvedor SEGURO
 *      (`resolverCultivoSeguro`, niveles A y B: el nombre oficial o una
 *      traducción/plural de UN solo cultivo curado: «Pomodoro», «Tomates»,
 *      «Cucumber»). Erratas, tipos, «Pimiento Palermo» o «Varios» NO se
 *      resuelven: salen tal cual, que es lo honrado.
 *   2. Con código: los nombres del catálogo en ese idioma (`names_i18n`, si quien
 *      llama los tiene); la traducción de la app de los 9 cultivos por defecto
 *      (`cropCycles.defaultCrops.*`, la que ya ve el usuario en los selectores);
 *      la etiqueta curada (`curados.ts`); en castellano, el nombre visible; si no
 *      se sabe en ese idioma, el nombre guardado («Acelga» en inglés: no se
 *      inventa una traducción).
 *   3. Sin código: lo de antes que sí era exacto (la clave tal cual, o una
 *      traducción de la app en el idioma de la pantalla) y si no, el nombre tal cual.
 *
 * En los INFORMES (costes, campañas, panel) solo se llama para grupos CON código:
 * un grupo por nombre se enseña como se escribió, como hoy, para que «Pomodoro»
 * y «tomate» (dos grupos sin código) no salgan los dos como «Tomate».
 *
 * PURO: sin red ni React; `t` lo pasa quien llama (react-i18next o i18next).
 */
import { CULTIVOS_CURADOS, cultivoCuradoPorCodigo, type IdiomaCultivo } from "./curados";
import { resolverCultivoSeguro } from "./resolver";
import { esCodigoCultivo } from "./grupo";

/** Lo mínimo de una función de traducción (la de react-i18next encaja). */
export type TraducirFn = (clave: string) => string;

/**
 * Las claves i18n de los 9 cultivos por defecto (`cropCycles.defaultCrops.*`).
 * F9 (05-10-2026): salen de la lista curada (`i18nKey`), no de una copia a mano
 * de la de `CropCatalogSelect` (retirado): una sola lista de cultivos.
 */
export const CLAVES_CULTIVO_POR_DEFECTO: readonly string[] = CULTIVOS_CURADOS.flatMap((c) => (c.i18nKey ? [c.i18nKey] : []));

export interface OpcionesEtiqueta {
  /** `crop_catalog.names_i18n` del cultivo, si quien llama lo tiene a mano. */
  namesI18n?: Partial<Record<IdiomaCultivo, string>> | null;
}

/** «it-IT» → «it»; vacío → «es». */
export function idiomaBase(lang: string | null | undefined): string {
  const l = String(lang ?? "").split(/[-_]/)[0].toLowerCase();
  return l || "es";
}

/** `t(clave)` solo si de verdad está traducida (i18next devuelve la clave si falta). */
function traducida(t: TraducirFn | null | undefined, clave: string): string | null {
  if (!t) return null;
  try {
    const v = t(clave);
    return typeof v === "string" && v && v !== clave ? v : null;
  } catch {
    return null;
  }
}

/** El código del cultivo: el del ciclo o, si no lo tiene, el de un nombre SEGURO (niveles A y B). */
export function codigoParaEtiqueta(code: number | null | undefined, nombre: string | null | undefined): number | null {
  if (esCodigoCultivo(code)) return code;
  if (!nombre || !String(nombre).trim()) return null;
  const r = resolverCultivoSeguro(nombre);
  return r.code;
}

/**
 * «Pepino» en inglés → «Cucumber» (no «Pepper»); 197 en italiano → «Pomodoro»;
 * 166 ACELGA en inglés → «Acelga» (sin traducción conocida); «Pepino Popits» → tal cual.
 */
export function etiquetaCultivo(
  code: number | null | undefined,
  nombre: string | null | undefined,
  t?: TraducirFn | null,
  lang?: string | null,
  opciones: OpcionesEtiqueta = {},
): string {
  const idioma = idiomaBase(lang) as IdiomaCultivo;
  const crudo = nombre == null ? "" : String(nombre);
  const c = codigoParaEtiqueta(code, crudo);

  if (c != null) {
    const delCatalogo = opciones.namesI18n?.[idioma];
    if (delCatalogo && delCatalogo.trim()) return delCatalogo;
    const curado = cultivoCuradoPorCodigo(c);
    if (curado?.i18nKey) {
      const app = traducida(t, `cropCycles.defaultCrops.${curado.i18nKey}`);
      if (app) return app;
    }
    const etiqueta = curado?.etiquetas?.[idioma];
    if (etiqueta) return etiqueta;
    if (idioma === "es" && curado) return curado.visible;
    return crudo.trim() || curado?.visible || crudo;
  }

  if (!crudo.trim()) return crudo.trim();
  // Sin código: lo que la heurística vieja hacía bien (la clave tal cual o una traducción exacta).
  const directa = traducida(t, `cropCycles.defaultCrops.${crudo.toLowerCase().replace(/\s+/g, "")}`);
  if (directa) return directa;
  const bajo = crudo.trim().toLowerCase();
  for (const k of CLAVES_CULTIVO_POR_DEFECTO) {
    const v = traducida(t, `cropCycles.defaultCrops.${k}`);
    if (v && v.toLowerCase() === bajo) return v;
  }
  return crudo;
}
